import React, { useCallback, useEffect, useState } from "react";
import {
    Typography,
    Button,
    Box,
    Paper,
    Grid,
    CardMedia,
    TextField,
    MenuItem,
    Card,
    CardContent,
    CardActions,
    Backdrop,
    Chip,
    InputAdornment,
    Tabs,
    Tab,
} from "@mui/material";
import Select from "react-select";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Pagination from "@mui/material/Pagination";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import Layout from "../../../Layout";

const SORT_OPTIONS = [
    { value: "desc", label: "Descending" },
    { value: "asc", label: "Ascending" },
];

const VERTICAL_NAME_MAPPING = {
    "CS": "Cyber Security",
    "DS": "Data Science",
    "PM": "Product Management",
    "SM": "Senior Management",
};

export const USER_CATEGORY_TO_VERTICAL_MAPPING = {
    "DS": [1, 2, 7],
    "PM": [3, 4, 9, 20, 26, 6, 8],
    "CS": [17, 36],
    "SM": [12, 22, 39, 52, 55, 56],
    "Business Management": [11, 16, 18, 44],
    "CXO": [30, 31, 37, 40, 51, 60, 63],
    "Strategy Leadership": [21, 25, 42, 54, 61, 67, 69],
    "General Management": [13, 43, 15, 44],
    "Human Resource": [10, 32, 34, 46, 62],
    "Project Management": [45, 28, 48, 49, 50, 53],
    "Artificial Intelligence": [38, 35, 41, 47, 59, 57, 58, 68, 70],
    "Finance": [24, 29],
    "Digital Transformation": [14, 19, 27],
};

const getDisplayName = (shortName) => {
    return VERTICAL_NAME_MAPPING[shortName] || shortName;
};

const getVerticalFromUserCategory = (userCategory) => {
    for (const [vertical, categories] of Object.entries(USER_CATEGORY_TO_VERTICAL_MAPPING)) {
        if (categories.includes(userCategory)) {
            return vertical;
        }
    }
    return null;
};

export const getDriveViewUrl = (url) => {
    if (!url || typeof url !== "string") return null;

    const trimmedUrl = url.trim();
    if (!trimmedUrl) return null;

    try {
        const parsed = new URL(trimmedUrl);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
            return null;
        }

        const isGoogleDrive = /(^|\.)(drive)\.google\.com$/.test(parsed.hostname);

        if (isGoogleDrive) {
            // Check if the URL is already in /file/d/FILE_ID format
            const fileDMatch = trimmedUrl.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
            if (fileDMatch && fileDMatch[1]) {
                return `https://drive.google.com/file/d/${fileDMatch[1]}/view`;
            }

            // Check for 'id' parameter (e.g. drive.google.com/uc?id=FILE_ID)
            const fileId = parsed.searchParams.get("id");
            if (fileId) {
                return `https://drive.google.com/file/d/${fileId}/view`;
            }

            return null;
        }

        return trimmedUrl;
    } catch {
        return null;
    }
};

const ResourceCenter = () => {
    const [verticalMapping, setVerticalMapping] = useState(null);
    const [subcategoriesDisplay, setSubcategoriesDisplay] = useState([]); // For UI display (deduplicated by name)
    const [subcategoriesMap, setSubcategoriesMap] = useState({}); // Maps display name to array of IDs
    const [allResources, setAllResources] = useState([]);
    const [filteredAndSorted, setFilteredAndSorted] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(12);
    const [displayData, setDisplayData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOrder, setSortOrder] = useState("desc");
    const [open, setOpen] = useState(false);
    const [selectedVerticals, setSelectedVerticals] = useState([]);
    const [selectedSubcategoryName, setSelectedSubcategoryName] = useState(null); // Track by display name
    const [isInitialized, setIsInitialized] = useState(false);
    const [verticalOptions, setVerticalOptions] = useState([]);

    const API_BASE = process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD;
    const API_CONFIG = {
        headers: {
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json",
        },
    };

    // Step 1: Fetch vertical mapping and initialize
    useEffect(() => {
        const fetchVerticalMapping = async () => {
            try {
                const res = await axios.post(
                    `${API_BASE}/resourcecenter/getverticalmapping`,
                    {},
                    API_CONFIG
                );
                setVerticalMapping(res.data);

                const options = Object.keys(res.data).map((vertical) => ({
                    value: vertical,
                    label: getDisplayName(vertical),
                }));
                setVerticalOptions(options);

                const userCategory = localStorage.getItem("category");
                let initialVerticals = [];

                if (userCategory) {
                    const mappedVertical = getVerticalFromUserCategory(parseInt(userCategory));
                    if (mappedVertical && res.data[mappedVertical]) {
                        initialVerticals = [mappedVertical];
                    } else {
                        initialVerticals = [Object.keys(res.data)[0]];
                    }
                } else {
                    initialVerticals = [Object.keys(res.data)[0]];
                }

                setSelectedVerticals(initialVerticals);
                setIsInitialized(true);
            } catch (error) {
                console.error("Error fetching vertical mapping:", error);
            }
        };

        fetchVerticalMapping();
    }, []);

    // Step 2: Fetch subcategories from ALL selected verticals (keep ALL IDs)
    useEffect(() => {
        if (!isInitialized) return;

        if (selectedVerticals.length === 0) {
            setSubcategoriesDisplay([]);
            setSubcategoriesMap({});
            setSelectedSubcategoryName(null);
            setAllResources([]);
            setFilteredAndSorted([]);
            setDisplayData([]);
            setCurrentPage(1);
            return;
        }

        const fetchSubcategoriesForVerticals = async () => {
            try {
                setOpen(true);

                // Get ALL category IDs from ALL selected verticals
                const allCategoryIds = selectedVerticals.flatMap(
                    (vertical) => verticalMapping[vertical]?.map((cat) => cat.id) || []
                );

                if (allCategoryIds.length === 0) {
                    setSubcategoriesDisplay([]);
                    setSubcategoriesMap({});
                    setSelectedSubcategoryName(null);
                    setAllResources([]);
                    setFilteredAndSorted([]);
                    setDisplayData([]);
                    setOpen(false);
                    return;
                }

                // Fetch subcategories for ALL category IDs in parallel
                const subcategoryRequests = allCategoryIds.map((catId) =>
                    axios.post(
                        `${API_BASE}/resourcecenter/getsubcategories`,
                        { categoryId: catId },
                        API_CONFIG
                    )
                );

                const responses = await Promise.all(subcategoryRequests);

                // Combine subcategories and track ALL IDs for each display name
                const displayMap = {}; // Maps display name to array of IDs
                const displayNameOrder = []; // Track order of appearance

                responses.forEach((res) => {
                    if (Array.isArray(res.data)) {
                        res.data.forEach((sub) => {
                            const displayName = sub.subcategory_name;
                            if (!displayMap[displayName]) {
                                displayMap[displayName] = [];
                                displayNameOrder.push(displayName);
                            }
                            // Add ID only if not already in this map
                            if (!displayMap[displayName].includes(sub.id)) {
                                displayMap[displayName].push(sub.id);
                            }
                        });
                    }
                });

                setSubcategoriesMap(displayMap);

                // Create display array maintaining order
                const displaySubcategories = displayNameOrder.map((name) => ({
                    name,
                    ids: displayMap[name],
                }));

                setSubcategoriesDisplay(displaySubcategories);

                // Auto-select first subcategory by name
                if (displayNameOrder.length > 0) {
                    setSelectedSubcategoryName(displayNameOrder[0]);
                } else {
                    setSelectedSubcategoryName(null);
                }

                setCurrentPage(1);
            } catch (error) {
                console.error("Error fetching subcategories:", error);
                setSubcategoriesDisplay([]);
                setSubcategoriesMap({});
                setSelectedSubcategoryName(null);
                setAllResources([]);
                setFilteredAndSorted([]);
                setDisplayData([]);
            } finally {
                setOpen(false);
            }
        };

        fetchSubcategoriesForVerticals();
    }, [selectedVerticals, isInitialized, verticalMapping]);

    // Step 3: Fetch resources with ALL subcategory IDs
    useEffect(() => {
        if (!selectedSubcategoryName || Object.keys(subcategoriesMap).length === 0) {
            setAllResources([]);
            setFilteredAndSorted([]);
            setDisplayData([]);
            setCurrentPage(1);
            return;
        }

        const fetchResources = async () => {
            setOpen(true);
            try {
                // Get ALL IDs for this subcategory name
                const subcategoryIds = subcategoriesMap[selectedSubcategoryName] || [];

                console.log("Fetching resources for subcategoryIds:", subcategoryIds);

                const res = await axios.post(
                    `${API_BASE}/resourcecenter/fetchresourcecenter-v2`,
                    { subcategoryIds: subcategoryIds }, // Send ALL IDs
                    API_CONFIG
                );

                console.log("Resources received:", res.data);

                setAllResources(Array.isArray(res.data) ? res.data : []);
                setCurrentPage(1);
                setSearchTerm("");
                setSortOrder("desc");
            } catch (error) {
                console.error("Error fetching resources:", error);
                setAllResources([]);
            } finally {
                setOpen(false);
            }
        };

        fetchResources();
    }, [selectedSubcategoryName, subcategoriesMap]);

    // Step 4: Apply search and sort
    useEffect(() => {
        let processed = [...allResources];

        if (searchTerm) {
            processed = processed.filter((val) =>
                val.hadding.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        processed.sort((a, b) => {
            if (sortOrder === "asc") {
                return a.hadding.localeCompare(b.hadding);
            } else {
                return b.hadding.localeCompare(a.hadding);
            }
        });

        setFilteredAndSorted(processed);
        setCurrentPage(1);
    }, [allResources, searchTerm, sortOrder]);

    // Step 5: Pagination
    useEffect(() => {
        const startIdx = (currentPage - 1) * pageSize;
        const endIdx = startIdx + pageSize;
        setDisplayData(filteredAndSorted.slice(startIdx, endIdx));
    }, [currentPage, filteredAndSorted, pageSize]);

    const handleVerticalDropdownChange = (selectedOptions) => {
        const verticals = selectedOptions.map((option) => option.value);
        setSelectedVerticals(verticals);
        setSelectedSubcategoryName(null);
    };

    const handleSubcategoryTabChange = (event, newTabIndex) => {
        if (subcategoriesDisplay.length > newTabIndex) {
            setSelectedSubcategoryName(subcategoriesDisplay[newTabIndex].name);
        }
    };

    const handleViewPdf = (url) => {
        const viewUrl = getDriveViewUrl(url);
        if (viewUrl) {
            window.open(viewUrl, "_blank", "noopener,noreferrer");
        }
    };

    const handleImageDownload = (url, docid, name) => {
        setOpen(true);
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        a.click();

        axios
            .post(
                `${API_BASE}/resourcecenter/updatecount-v2`,
                { docid },
                API_CONFIG
            )
            .then(() => {
                setAllResources((prev) =>
                    prev.map((item) =>
                        item.docid === docid ? { ...item, count: item.count + 1 } : item
                    )
                );
            })
            .catch(console.error)
            .finally(() => setOpen(false));
    };

    const getResourceVertical = (resource) => {
        // This assumes your API returns a vertical field
        if (resource.vertical) {
            return resource.vertical;
        }

        // Fallback: Find the vertical by checking which selected vertical contains this resource
        for (const vertical of selectedVerticals) {
            const categoryIds = verticalMapping[vertical]?.map((cat) => cat.id) || [];
            if (categoryIds.length > 0) {
                // If you have category info in resource, check against it
                // Otherwise you may need to track this differently
            }
        }

        // If only one vertical is selected, use that
        if (selectedVerticals.length === 1) {
            return selectedVerticals[0];
        }

        return null;
    };


    const totalPages = Math.ceil(filteredAndSorted.length / pageSize);

    if (!verticalMapping || !isInitialized) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    const selectedDropdownOptions = selectedVerticals.map((vertical) => ({
        value: vertical,
        label: getDisplayName(vertical),
    }));

    // Find the current tab index based on selectedSubcategoryName
    const currentTabIndex = subcategoriesDisplay.findIndex(
        (sub) => sub.name === selectedSubcategoryName
    );

    return (
        <>
            {/* <LoginDashboard /> */}
            <Layout initialCollapsed={false}>
                <Box
                    sx={{
                        backgroundColor: "#f0f4f8",
                        minHeight: "100vh",
                        pt: 8,
                        pb: 5,
                    }}
                >
                    <Grid container>
                        <Grid item lg={3} sx={{ display: { xs: "none", lg: "block" } }}>
                            {/* <Sidebar /> */}
                        </Grid>
                        <Grid item lg={12} xs={12}>
                            <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 } }}>
                                {/* Header */}
                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: "12px",
                                        p: { xs: 2, md: 3 },
                                        mb: 3,
                                        background: "#fff",
                                        border: "1px solid #e8eef7",
                                        borderLeft: "4px solid #1A73E8",
                                    }}
                                >
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={8}>
                                            <Typography
                                                variant="h4"
                                                sx={{
                                                    fontWeight: 700,
                                                    mb: 0.5,
                                                    fontSize: { xs: "1.5rem", md: "2.5rem" },
                                                    color: "#1a1a1a",
                                                }}
                                            >
                                                Resource Center
                                            </Typography>
                                            <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
                                                {/* <Typography sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" }, color: "#666" }}>
                                                Dashboard
                                            </Typography>
                                            <RemoveIcon sx={{ fontSize: "0.875rem", color: "#999" }} />
                                            <Typography sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" }, color: "#666" }}>
                                                Resource Centre
                                            </Typography> */}
                                                {/* <RemoveIcon sx={{ fontSize: "0.875rem", color: "#999" }} /> */}
                                                {/* <Typography sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" }, fontWeight: 600, color: "#0052cc" }}>
                                                {selectedVerticals.map((v) => getDisplayName(v)).join(", ")}
                                            </Typography> */}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={4} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                                            <CardMedia
                                                component="img"
                                                image="https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/Student-Dashboard/pdficon.webp"
                                                alt="PDF Icon"
                                                sx={{
                                                    width: { xs: "60px", md: "80px" },
                                                    height: "auto",
                                                    mx: { xs: 0, sm: "auto" },
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {/* React Select Dropdown for Verticals */}
                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: "12px",
                                        p: { xs: 2, md: 2.5 },
                                        mb: 3,
                                        border: "1px solid #e8eef7",
                                        backgroundColor: "#fff",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            mb: 1.5,
                                            fontWeight: 700,
                                            fontSize: { xs: "0.95rem", md: "1.1rem" },
                                            color: "#1a1a1a",
                                        }}
                                    >
                                        Select Verticals
                                    </Typography>
                                    <Select
                                        isMulti
                                        options={verticalOptions}
                                        value={selectedDropdownOptions}
                                        onChange={handleVerticalDropdownChange}
                                        placeholder="Search and select verticals..."
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                borderRadius: "8px",
                                                borderColor: "#e8eef7",
                                                boxShadow: "none",
                                                backgroundColor: "#f5f7fa",
                                                "&:hover": {
                                                    borderColor: "#1A73E8",
                                                },
                                                "&:focus": {
                                                    borderColor: "#1A73E8",
                                                    boxShadow: "0 0 0 3px rgba(0, 82, 204, 0.1)",
                                                },
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                zIndex: 99999999
                                            }),
                                            option: (base, state) => ({
                                                ...base,
                                                backgroundColor: state.isSelected
                                                    ? "#1A73E8"
                                                    : state.isFocused
                                                        ? "#f0f3f7"
                                                        : "#fff",
                                                color: state.isSelected ? "#fff" : "#1a1a1a",
                                                cursor: "pointer",
                                            }),
                                            multiValue: (base) => ({
                                                ...base,
                                                backgroundColor: "#e8eef7",
                                                borderRadius: "6px",
                                            }),
                                            multiValueLabel: (base) => ({
                                                ...base,
                                                color: "#1A73E8",
                                                fontWeight: 500,
                                            }),
                                            multiValueRemove: (base) => ({
                                                ...base,
                                                color: "#1A73E8",
                                                cursor: "pointer",
                                                "&:hover": {
                                                    backgroundColor: "#1A73E8",
                                                    color: "#fff",
                                                },
                                            }),
                                        }}
                                    />
                                </Paper>

                                {/* Subcategory Tabs - TAB DESIGN */}
                                {subcategoriesDisplay.length > 0 && (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            borderRadius: "12px",
                                            mb: 3,
                                            border: "1px solid #e8eef7",
                                            backgroundColor: "#fff",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <Tabs
                                            value={currentTabIndex >= 0 ? currentTabIndex : 0}
                                            onChange={handleSubcategoryTabChange}
                                            variant="scrollable"
                                            scrollButtons="auto"
                                            sx={{
                                                borderBottom: "2px solid #e8eef7",
                                                "& .MuiTabs-flexContainer": {
                                                    px: { xs: 1.5, md: 2.5 },
                                                },
                                                "& .MuiTab-root": {
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    fontSize: { xs: "0.85rem", md: "0.95rem" },
                                                    color: "#666",
                                                    py: 1.5,
                                                    px: { xs: 1.5, md: 2 },
                                                    minWidth: { xs: "80px", md: "100px" },
                                                    transition: "all 0.3s ease",
                                                    "&:hover": {
                                                        color: "#1A73E8",
                                                        backgroundColor: "#f9fbff",
                                                    },
                                                    "&.Mui-selected": {
                                                        color: "#1A73E8",
                                                        fontWeight: 700,
                                                    },
                                                },
                                                "& .MuiTabs-indicator": {
                                                    backgroundColor: "#1A73E8",
                                                    height: "3px",
                                                },
                                            }}
                                        >
                                            {subcategoriesDisplay.map((sub, index) => (
                                                <Tab
                                                    key={sub.name}
                                                    label={sub.name}
                                                    id={`subcategory-tab-${index}`}
                                                    aria-controls={`subcategory-panel-${index}`}
                                                />
                                            ))}
                                        </Tabs>
                                    </Paper>
                                )}

                                {/* Search & Filter */}
                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: "12px",
                                        p: { xs: 2, md: 2.5 },
                                        mb: 4,
                                        border: "1px solid #e8eef7",
                                        backgroundColor: "#fff",
                                    }}
                                >
                                    <Grid container spacing={{ xs: 1.5, md: 2 }}>
                                        <Grid item xs={12} sm={6} md={5}>
                                            <TextField
                                                fullWidth
                                                placeholder="Search resources..."
                                                size="small"
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    setSearchTerm(e.target.value);
                                                    setCurrentPage(1);
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon sx={{ color: "#999", fontSize: "1.2rem" }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "8px",
                                                        backgroundColor: "#f5f7fa",
                                                        transition: "all 0.3s ease",
                                                        "&:hover": {
                                                            backgroundColor: "#f0f3f7",
                                                        },
                                                        "&.Mui-focused": {
                                                            backgroundColor: "#fff",
                                                            boxShadow: "0 0 0 3px rgba(0, 82, 204, 0.1)",
                                                        },
                                                    },
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: "#e8eef7",
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                select
                                                fullWidth
                                                label="Sort Order"
                                                value={sortOrder}
                                                onChange={(e) => {
                                                    setSortOrder(e.target.value);
                                                    setCurrentPage(1);
                                                }}
                                                size="small"
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "8px",
                                                        backgroundColor: "#f5f7fa",
                                                    },
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: "#e8eef7",
                                                    },
                                                }}
                                            >
                                                {SORT_OPTIONS.map((opt) => (
                                                    <MenuItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    height: "100%",
                                                    justifyContent: { xs: "flex-start", md: "flex-end" },
                                                }}
                                            >
                                                <Chip
                                                    label={`${filteredAndSorted.length} resources`}
                                                    variant="outlined"
                                                    sx={{
                                                        borderColor: "#0052cc",
                                                        color: "#0052cc",
                                                        fontWeight: 500,
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                <Backdrop
                                    sx={{
                                        color: "#fff",
                                        zIndex: (theme) => theme.zIndex.drawer + 1,
                                    }}
                                    open={open}
                                >
                                    <CircularProgress color="inherit" />
                                </Backdrop>

                                {/* Resources Grid */}
                                
{/* Resources Grid */}
<Box sx={{ mb: 3 }}>
  {displayData.length > 0 ? (
    <Grid container spacing={{ xs: 1.5, md: 1.75 }}>
      {displayData.map((rcdoc) => (
        <Grid item xs={12} sm={6} md={4} key={rcdoc.docid}>
          <Card
            sx={{
              height: 148,
              display: "flex",
              flexDirection: "column",
              borderRadius: "9px",
              border: "1px solid #e1e8f2",
              boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
              transition: "border-color 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                borderColor: "#1A73E8",
                boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
              },
            }}
          >
            <CardContent
              sx={{
                p: 1.25,
                pb: "8px !important",
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
              }}
            >
              {/* Title and download icon */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0.75,
                  minHeight: 30,
                }}
              >
                <CardMedia
                  component="img"
                  image={
                    rcdoc.file_type === "pdf"
                      ? "../Rcimages/pdf.svg"
                      : rcdoc.file_type === "image"
                        ? "../Rcimages/png.svg"
                        : "../Rcimages/ppt.png"
                  }
                  alt={rcdoc.file_type}
                  sx={{
                    width: 20,
                    height: 22,
                    flexShrink: 0,
                  }}
                />

                <Typography
                  variant="subtitle2"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    lineHeight: 1.25,
                    color: "#111827",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {rcdoc.hadding}
                </Typography>

                <Button
                  aria-label={`Download ${rcdoc.hadding}`}
                  onClick={() =>
                    handleImageDownload(
                      rcdoc.file_link,
                      rcdoc.docid,
                      rcdoc.hadding
                    )
                  }
                  sx={{
                    minWidth: 22,
                    width: 22,
                    height: 22,
                    p: 0,
                    flexShrink: 0,
                    color: "#1A73E8",
                    "&:hover": {
                      backgroundColor: "#eef5ff",
                    },
                  }}
                >
                  <DownloadIcon sx={{ fontSize: 16 }} />
                </Button>
              </Box>

              {/* Description */}
              <Box
                sx={{
                  mt: 0.5,
                  mb: 0.5,
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  pr: 0.5,
                  "&::-webkit-scrollbar": {
                    width: "3px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#cbd5e1",
                    borderRadius: "3px",
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.68rem",
                    color: "#4b5563",
                    lineHeight: 1.4,
                  }}
                >
                  {rcdoc.description}
                </Typography>
              </Box>

              {/* View button and resource details */}
              <CardActions
                sx={{
                  p: 0,
                  mt: "auto",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  gap: 1,
                  minHeight: 24,
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    <VisibilityIcon sx={{ fontSize: "14px !important" }} />
                  }
                  onClick={() => handleViewPdf(rcdoc.file_link)}
                  sx={{
                    minHeight: 22,
                    px: 1.1,
                    py: 0.25,
                    borderRadius: "4px",
                    backgroundColor: "#1A73E8",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.68rem",
                    lineHeight: 1.2,
                    boxShadow: "none",
                    "& .MuiButton-startIcon": {
                      mr: 0.5,
                      ml: 0,
                    },
                    "&:hover": {
                      backgroundColor: "#155fc0",
                      boxShadow: "none",
                    },
                  }}
                >
                  View
                </Button>

                <Box sx={{ textAlign: "right", lineHeight: 1.1 }}>
                  <Typography
                    component="div"
                    sx={{
                      fontSize: "0.52rem",
                      color: "#9ca3af",
                      lineHeight: 1.2,
                    }}
                  >
                    {rcdoc.count || 0} Downloads
                  </Typography>

                  <Typography
                    component="div"
                    sx={{
                      fontSize: "0.58rem",
                      color: "#1A73E8",
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}
                  >
                    {rcdoc.file_size?.value}
                    {rcdoc.file_size?.unit}
                  </Typography>
                </Box>
              </CardActions>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  ) : (
    <Paper
      elevation={0}
      sx={{
        textAlign: "center",
        py: 8,
        borderRadius: "12px",
        border: "1px solid #e8eef7",
        backgroundColor: "#f9fbff",
      }}
    >
      <Typography sx={{ color: "#999", fontSize: "1.1rem" }}>
        No resources found
      </Typography>
    </Paper>
  )}
</Box>

                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Layout>
        </>
    );
};

export default ResourceCenter;
